module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('p', {
    staticClass: "CN",
    attrs: {
      "id": "ch001"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_1"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch-001"
    }
  }, [_c('span', {
    staticClass: "num"
  }, [_vm._v("1")]), _c('span', {
    staticClass: "space05"
  }, [_vm._v("Beyond Belief")])])]), _vm._v(" "), _c('p', {
    staticClass: "CT"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch-001"
    }
  }, [_vm._v("Paul’s Dynamic Faith Language")])]), _vm._v(" "), _c('p', {
    staticClass: "CE"
  }, [_vm._v("I believe in, "), _c('i', [_vm._v("credo in")]), _vm._v(", means that I am not alone. In our glory and in our misery we men are not alone. God comes to meet us and as our Lord and Master. He comes to our aid. . . . One way or other, I am in all circumstances in company with Him. . . . Of ourselves we cannot achieve, have not achieved, and shall not achieve a togetherness with Him; that we have not deserved that He should be our God, have no power of disposal and no rights over Him, but that with unowed kindness, in the freedom of His majesty, He resolved of His own self to be man’s God, our God.")]), _vm._v(" "), _c('p', {
    staticClass: "CC"
  }, [_vm._v("—Karl Barth, "), _c('i', [_vm._v("Dogmatics in Outline")])]), _vm._v(" "), _c('p', {
    staticClass: "CE1"
  }, [_vm._v("To have faith is to be open, vulnerable. It is also to wince and to withdraw. For this reason, faithfulness has a plodding quality. It is more stick-to-itiveness than it is power and glory. The key to faith is persistence. In the face of sin—be it outright evil or simple distraction—it is very difficult to persevere in faith. In the presence of temptation—be it desire or disaster—it is difficult to remain faithful. Yet, for the religious Jew, there is only teshuvah, repentance or re-turning, as the compass needle always returns toward the north.")]), _vm._v(" "), _c('p', {
    staticClass: "CC"
  }, [_vm._v("—David Blumenthal, “The Place of Faith and Grace in Judaism”")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("In St. Augustine’s sermon on John 20, in view of the resurrection appearances of Jesus to his disciples, he addresses the obvious problem of a human (risen) Jesus passing through a wall to present himself to his disciples. "), _c('i', [_vm._v("How is this possible?")]), _vm._v(" Augustine made appeal to the uniqueness of Jesus even before his death and resurrection (whereby, for example, he could walk on water, though he had normal body weight). But he finishes his thought "), _c('a', {
    attrs: {
      "id": "page_2"
    }
  }, [_vm._v("with a striking notion: “Where reason fails, faith builds up.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn1",
      "id": "fn1"
    }
  }, [_vm._v("1")]), _vm._v(" Augustine himself was far from being opposed to reason,"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn2",
      "id": "fn2"
    }
  }, [_vm._v("2")])]), _vm._v(" but there has admittedly been a problematic tension in the history of Christianity between faith and reason. There is, of course, very good reason why the term “faith” has become a distinctively Christian word. After all, the word "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(", the Greek word often translated “faith,” is found hundreds of times in the New Testament (and over thirty-five times in Romans alone). Important words like “faith,” though, when overused, tend to get flattened out and have meanings and connotations attributed to it that do not go back to the faith language of Scripture, or do not represent the depth and richness of that word (group). There are three problematic trends in the way Christians (and others) use faith language in religious ways.")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s1"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s1"
    }
  }, [_vm._v("Faith as Opinion")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("A few years ago, I taught a course that introduced the basics of Christianity to first-year college students. Not long after day one, students began debating issues like the historical reliability of the Gospels and the ability to prove the resurrection or divinity of Jesus. I was particularly taken aback when a student tried to end a debate by saying, “I don’t care about proving what I believe. I believe it by faith, and that should be enough.” As the course went on, I began to notice a trend whereby students used the language of faith in a way someone would use the word “opinion.” In that sense, faith became a means of occluding conversation of an academic nature by removing any grounds for debate. “I believe it by faith,” in that context, meant that reasons were not needed, even perhaps that "), _c('i', [_vm._v("reason")]), _vm._v(" wasn’t needed. But is that what Paul meant when he talked about faith? I am afraid that many unknowingly buy into Mark Twain’s facetious dictum: "), _c('i', [_vm._v("faith is believing what you know ain’t so")]), _vm._v(". We must return to the New Testament, and especially Paul, to see how a proper conception of Christian faith operates. What is the relationship between faith and reason?")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s2"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_3"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s2"
    }
  }, [_vm._v("Faith as Doctrine")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("A second typical use of faith language in modern religious vocabulary pertains to things like faith statements and faith traditions. Undoubtedly, we get this terminology from the early creeds—doctrinal statements that begin “I believe” (Latin "), _c('i', [_vm._v("credo")]), _vm._v("). Thus, faith can be nearly synonymous with the language of doctrine or religion, as in “interfaith dialogue.” This is not an unreasonable association based on certain language in the New Testament (e.g., 1 Tim 4:6), but this kind of thinking, where faith equals doctrine, could degenerate into a kind of checklist mentality. This can turn faith into something sterile, purely cerebral, and even gnostic. In his book "), _c('i', [_vm._v("The Creed")]), _vm._v(", Luke Timothy Johnson makes the point that "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" is often translated “faith” in English versions of the New Testament, but, in fact, the Greek word has a broad range of meaning that covers a wide spectrum: belief, trust, endurance, loyalty, obedience. When we translate "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" "), _c('i', [_vm._v("only")]), _vm._v(" as “belief,” the polyvalent nature of the term is suppressed and the cognitive dimension often dominates. Johnson finds this way of flatly translating or understanding "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" deeply problematic in view of understanding the true nature of Christian confession and life.")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("One can hold a belief that something is true without letting the belief matter to one’s life. The entire Christian creed can be treated as a set of beliefs that amount to no more than interesting opinions. This is the sort of faith that the letter of James scorns: “You believe that God is one. You do well. Even the demons believe—and shudder!” (Jas 2:19 NRSV)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn3",
      "id": "fn3"
    }
  }, [_vm._v("3")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_vm._v("Alternatively, Johnson explains that "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" involves a “response of the whole person.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn4",
      "id": "fn4"
    }
  }, [_vm._v("4")])]), _vm._v(" Part of what I wish to call for, then, is a patient (re)reading of Paul, the most "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("-focused writer in the New Testament, in order to understand best how and why he employed "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" terminology.")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s3"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_4"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s3"
    }
  }, [_vm._v("Faith as Passive")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("You might not be surprised to know that Martin Luther was fond of using faith language in reference to his conception of Christianity. In narrating his own epiphany about the true nature of righteousness through Christ, Luther writes:")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("At last, by the mercy of God, meditating day and night, I gave heed to the words, namely, “In it the righteousness of God is revealed, as it is written, ‘He who through faith is righteous shall live.’ ” There I began to understand that the righteousness of God is revealed by the gospel, namely, the passive righteousness with which a merciful God justifies us by faith, as it is written, “He who through faith is righteous shall live.” Here I felt that I was altogether born again and had entered paradise itself through open gates. There a totally other face of the entire Scripture showed itself to me."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn5",
      "id": "fn5"
    }
  }, [_vm._v("5")])])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("As Alister McGrath explains, Luther understood justification to involve the transference of an alien righteousness to unrighteous persons by faith: “We are passive, and God is active, in our justification. Grace gives, and faith gratefully receives—and even that faith must itself be seen as a gracious gift of God.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn6",
      "id": "fn6"
    }
  }, [_vm._v("6")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The heart of this notion of a passive receipt of righteousness seems close to how some characterize the nature of faith itself (though I do not think Luther himself ultimately favored a notion of “passive faith”; see 24–27). For example, Old Testament scholar Walter Kaiser summarizes the nonmeritorious acceptance of God’s grace in view of the exemplary faith of Abraham:")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("God does the accounting; God does the reckoning; God does the crediting; God does the justifying—the declaring of this man to be just. Abraham does nothing. God gave the promise which Abraham had only to receive. Sometimes the question is asked, Is believing a work in itself? Do we have “faith in faith”? Can we pull ourselves up by our own bootstraps? The answer, of course, is that faith is passive. It is a passive act. It is like receiving a Christmas present: we put out our hands to take, to accept, to receive. "), _c('a', {
    attrs: {
      "id": "page_5"
    }
  }, [_vm._v("There is nothing more than a passive act here. We don’t earn our Christmas presents. The same is true with faith."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn7",
      "id": "fn7"
    }
  }, [_vm._v("7")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("The New Testament writers clearly believed that God gave a gift in his grace that no one deserves. Thus, it is fitting to talk about believers as "), _c('i', [_vm._v("recipients")]), _vm._v(". But does the language of "), _c('i', [_vm._v("passivity")]), _vm._v(" fit the nature of Pauline "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("? Did the word "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Paul’s time communicate nonactivity, passivity, even a passive act?")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("One might appeal to Pauline language that juxtaposes faith and works (Rom 3:21–31; 9:32; Gal 2:16; cf. Eph 2:8–9). However, because of the complex history of the interpretation of Paul in the church and Western society, it is best to set a discussion of the Pauline language of faith ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(") on the right track by beginning with the Old Testament. Through a study that takes a particularly "), _c('i', [_vm._v("Jewish")]), _vm._v(" perspective on the language of faith, we can understand Paul’s theology in a way that goes beyond (merely) belief.")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s4"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s4"
    }
  }, [_vm._v("The Old Testament Foundation of Paul’s Use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις")])])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Two times Paul quotes Hab 2:4 (“the righteous will live by faith”) in order to make a point about a Christian life determined by "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" (Gal 3:11; Rom 1:17). That should be a key indicator that his own understanding of faith was highly (though not exclusively) influenced by the Septuagint, the Greek translation of the Old Testament that was, for all intents and purposes, what Paul read as Scripture."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn8",
      "id": "fn8"
    }
  }, [_vm._v("8")])]), _vm._v(" Thus, in order to make the most sense of what Paul meant when he used the language of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(", it is necessary to investigate how the Septuagint translators used this word, especially in view of Hebrew/Aramaic terms and ideas. A substantial discussion of the use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" is coming in chapter 3, but at this juncture it is crucial that a proper perspective on the scriptural use of faith language is adumbrated.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("A study of the relationship between the Greek Septuagint and the Hebrew Old Testament demonstrates that, while the Septuagint translators "), _c('a', {
    attrs: {
      "id": "page_6"
    }
  }, [_vm._v("used "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in relation to a number of Hebrew words, the three most common are "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמן")]), _vm._v(" ,"), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמונה")]), _vm._v(", and "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמת")]), _vm._v(". The first word, "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמן")]), _vm._v(", means “trust” or “reliability.” The second, "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמונה")]), _vm._v(", is quite similar."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn9",
      "id": "fn9"
    }
  }, [_vm._v("9")]), _vm._v(" In contexts related to human relationships, "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמונה")]), _vm._v(" “often refers to those who have the capacity to remain stable (i.e., faithful) amid the unsettling circumstances of life, realizing God’s truth has established them.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn10",
      "id": "fn10"
    }
  }, [_vm._v("10")])]), _vm._v(" For example, the term is used of Moses’s hands as Aaron and Hur supported him on the hill of Rephidim (Exod 17:12). Thus, his hands were "), _c('i', [_vm._v("firm")]), _vm._v(", "), _c('i', [_vm._v("steady")]), _vm._v(", "), _c('i', [_vm._v("reliable")]), _vm._v(".")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The third term, "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמת")]), _vm._v(", can be translated “faithfulness” or “dependability.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn11",
      "id": "fn11"
    }
  }, [_vm._v("11")])]), _vm._v(" Isaiah 38:3 furnishes a helpful example. Here Hezekiah, having taken ill, pleads before God with these words: “Remember now, O L"), _c('span', {
    staticClass: "small"
  }, [_vm._v("ORD")]), _vm._v(", I implore you, how I have walked before you in "), _c('i', [_vm._v("faithfulness")]), _vm._v(" ["), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמת")]), _vm._v("] with a whole heart, and have done what is good in your sight” (NRSV).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Walter Brueggemann stresses how the Old Testament language of faith is everywhere associated with covenant relationship. Within this construct, faith has less to do with theological ideas per se than with the nature and integrity of a relationship of trust. Brueggemann writes:")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("“Faith” concerns attentive engagement in a promissory relationship. Only rarely does the Old Testament suggest that “faith” is a body of teaching that Israel is to “believe.” Israel’s faith does not necessarily lack normative substance nor is it vacuous, but the relationship is more elemental than the substantive teaching which reflects upon that relationship. That in the Old Testament faith is regarded as “trust in” is more elemental than “assent to” is a matter often discounted in formal theological articulations, but “trust” is not to be understood primarily in emotive terms. Trust is a practice that entails obedience to Torah [the law] and its specific requirements. Israel’s fidelity to Yahweh, not unlike fidelity in marriage, thus consists of concrete acts that take the other party with defining seriousness."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn12",
      "id": "fn12"
    }
  }, [_vm._v("12")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_c('a', {
    attrs: {
      "id": "page_7"
    }
  }, [_vm._v("This brief glance at faith language in the Old Testament offers enough evidence to put into question all three of the problematic ways that many modern readers of Paul view such terminology. Faith is neither mere religious opinion nor only doctrinal ideas. And certainly no Israelite or Jew reading the Hebrew Old Testament or the Greek Septuagint would find the word “passive” applicable to the use of such words in the faith semantic category.")])]), _c('a', {
    attrs: {
      "id": "page_7"
    }
  }), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s5"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_7"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s5"
    }
  }, [_vm._v("Faith or Faithfulness?")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Given that the Hebrew terms behind the use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in the Septuagint (chap. 3) refer to words that are often best translated “faithfulness” (or loyalty, reliability, commitment), why is it that where "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" appears in Paul it is usually rendered “faith” by most English translations?"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn13",
      "id": "fn13"
    }
  }, [_vm._v("13")])]), _vm._v(" This question is central to the concerns of this book and one, I hope, that every student of Paul will examine carefully.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Since the default translation of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in most English translations is “faith,” it is instructive to observe where deviations tend to occur within these translations. First of all, nearly all translators are prone to translate "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" “faithfulness” when it relates to the nature and activity of God. In Rom 3:3, for example, Paul writes, “What if some were unfaithful ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ἠπίστησάν")]), _vm._v("]? Will their unfaithfulness ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ἀπιστία")]), _vm._v("] nullify God’s faithfulness ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("]?” Almost every modern English translation renders "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" “faithfulness” here, as it is in reference to the loyalty and reliability of God (e.g., NASB, NIV, ESV, RSV, NRSV, NET; cf. Hos 2:22 LXX).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("A second example in the letters of Paul is instructive, this time from Galatians. Near the close of the epistle, Paul contrasts the obvious works of the flesh (e.g., impurity, idolatry, hatred, jealousy, factions) with the “fruit of the Spirit”: love, joy, peace, patience, kindness, goodness, "), _c('i', [_vm._v("faithfulness")]), _vm._v(" ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("), gentleness, and self-control (5:22–23). Richard Longenecker summarizes what is probably the perspective of most scholars who translate "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" “faithfulness” here (but tend to render it “faith” everywhere else in Galatians):")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_c('i', [_vm._v("Pistis")]), _vm._v(", though used repeatedly elsewhere in Galatians to signify a person’s response of trust regarding God’s salvation provided in Christ Jesus, here undoubtedly means the ethical virtue of “faithfulness.” . . . Here . . . the "), _c('a', {
    attrs: {
      "id": "page_8"
    }
  }, [_vm._v("subject is the believer and the context is determinative. For situated, as it is, amid eight other nouns in a list of human virtues, "), _c('i', [_vm._v("pistis")]), _vm._v(" must here be understood as well as the human virtue of faithfulness, that is produced in the believer’s life by the faithful God through his Spirit."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn14",
      "id": "fn14"
    }
  }, [_vm._v("14")])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_vm._v("Longenecker states that faith (as a “response of trust”) is the sort of default meaning for "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Paul and that such a meaning is something sensibly distinct from faithfulness (which he implies is a less-common meaning of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" as it pertains to the believer). This appears similar to comments that Douglas Moo makes regarding the language of faith in Romans."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn15",
      "id": "fn15"
    }
  }, [_vm._v("15")])]), _vm._v(" It is almost as if there is a preference for viewing the operation of faith for Paul, almost by default, as something essentially internal (after all, Christians believe with their hearts and minds; Rom 10:10) or nonactive insofar as faithfulness would seem "), _c('i', [_vm._v("too")]), _vm._v(" active or "), _c('i', [_vm._v("too")]), _vm._v(" externally involved."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn16",
      "id": "fn16"
    }
  }, [_vm._v("16")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("In his commentary on Philemon, Markus Barth offers an interesting excursus on the interpretation of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Paul with a view toward whether the apostle means faith or faithfulness. Barth notes that it is not uncommon for Philemon commentators to treat the reference to "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Phlm 5 as “faith” unto the Lord Jesus: “I hear of your love for all the saints and your faith ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("] toward the Lord Jesus” (NRSV). As expected, all of the major English translations have “faith” here (e.g., ASV, NASB, NIV, ESV, RSV, NRSV, NET). In that case, the impression is that Philemon is commended for his love for his Christian brothers and sisters and for his “belief/faith” in Jesus Christ. Barth observes that, while the idea of belief is important for Paul, translating "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" as “faith” on this occasion may be missing his rhetorical and theological point. Because "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" can have the meaning “fidelity” or “loyalty,” we must entertain the possibility that this is what Paul refers to here."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn17",
      "id": "fn17"
    }
  }, [_vm._v("17")])]), _vm._v(" Appealing to texts like 1 Thess 1:3 (“work of faith”) and Rom 1:5 (“obedience of faith”), Barth underscores the critical point that in many cases obedience and faith are not as neatly separable human operations as some interpreters of "), _c('a', {
    attrs: {
      "id": "page_9"
    }
  }, [_vm._v("Paul presume them to be. With that in mind, when Paul uses "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ἀγάπη")]), _vm._v(" + "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(", Barth surmises that these are not meant to be treated separately (i.e., "), _c('i', [_vm._v("love")]), _vm._v(" for saints, "), _c('i', [_vm._v("faith")]), _vm._v(" in the Lord Jesus). Rather, it makes more sense to see them together, perhaps even as a hendiadys: “steadfast love which you have for the Lord Jesus and for all the saints.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn18",
      "id": "fn18"
    }
  }, [_vm._v("18")]), _vm._v(" Is Barth correct about this active nature of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Phlm 5? Should "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" be best understood in this context as “belief” (something essentially cognitive) or “faithfulness” (something active and encompassing the whole person and blending into the concept of obedience)?")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("Part of the confusion comes from the polyvalent nature of the word "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" itself. To simplify an explanation of how "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" can stretch to cover either belief or faithfulness, we might look at the key cognate Greek words that appear in the New Testament. On the one hand the verb "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πιστεύω")]), _vm._v(" means “I believe” (and almost never “I obey” or “I show faithfulness”)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn19",
      "id": "fn19"
    }
  }, [_vm._v("19")])]), _vm._v(" On the other hand the adjective "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πιστός")]), _vm._v(" means “faithful.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn20",
      "id": "fn20"
    }
  }, [_vm._v("20")])]), _vm._v(" In the New Testament, "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" can cover the spectrum of these cognate words. Thus, "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" can have at least two distinct (but related) meanings in Paul’s letters: belief and faithfulness.")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("Meaning #1: Believing Faith ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις")]), _vm._v(" as “Belief”)")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("While I express caution regarding the treatment of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" as a passive form of faith, there is sufficient evidence in Paul (and elsewhere in the New Testament) "), _c('a', {
    attrs: {
      "id": "page_10"
    }
  }, [_vm._v("to establish a connotation of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" focused on cognition, the proper operation of the mind and heart with respect to revelation and truth, what Markus Barth refers to as “an epistemological and hermeneutical sense.”"), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn21",
      "id": "fn21"
    }
  }, [_vm._v("21")]), _vm._v(" When "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" is used in this way, the emphasis falls on the proper method of perception, which is at odds with worldly knowledge and mere human ways of seeing reality (see chap. 6 on 1 Corinthians and chap. 7 on 2 Corinthians). Barth, quite appropriately, appeals to Heb 11:1, where "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" is defined as “the assurance of things hoped for, the conviction of things not seen” (NRSV)."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn22",
      "id": "fn22"
    }
  }, [_vm._v("22")])]), _vm._v(" When used in this way, "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" represents a kind of divinely enabled extrasensory perception, a second way of seeing and knowing. One can have confidence in what appears invisible—not because it is mere hunch or opinion, but because he or she has been given access to a perceptual key that unlocks a divine reality. Perhaps the best example of this, for Paul, comes in 2 Cor 5, where he proclaims “we walk by faith, not by sight” (5:7 NRSV). Paul commends a faith that goes beyond appearances, the mind must activate a new lens of perception."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn23",
      "id": "fn23"
    }
  }, [_vm._v("23")])]), _vm._v(" In chapters 6–7 below, I highlight occasions where Paul employs "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" with more emphasis on this kind of semantic nuance.")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("Meaning #2: Obeying Faith ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις")]), _vm._v(" as “Faithfulness”)")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("For this second shade of the meaning of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(", Barth appeals to the concept of covenant in the Old Testament and how faithfulness is a social platform central to the divine-human relationship, a relationship that expects love, goodwill, mutuality, and loyalty from both sides."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn24",
      "id": "fn24"
    }
  }, [_vm._v("24")])]), _vm._v(" It will be a major burden "), _c('a', {
    attrs: {
      "id": "page_11"
    }
  }, [_vm._v("of this book to make this case more extensively (see chap. 5), but for this brief description, let us look at Matt 23:23. In a denunciation of the disingenuous behavior and myopia of the scribes and Pharisees, Jesus makes this accusation: “Woe to you, experts in the law and you Pharisees, hypocrites! For you give a tenth of mint, dill, and cumin, yet you have neglected the weightier provisions of the law: justice, mercy, and faithfulness ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("]; but these are the things you should have done without neglecting the other” (NET)."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn25",
      "id": "fn25"
    }
  }, [_vm._v("25")]), _vm._v(" Ulrich Luz summarizes the perspective of most commentators on why "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" here ought to be translated “faithfulness” (and not “faith”):")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("“Faithfulness/faith” here cannot mean faith in Jesus, nor can it meant the faith of prayer or the active faith that performs works of love, for faith is never for Matthew the essence of the requirements of the law. Instead, we are to understand ["), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("] in the tradition of biblical language, but also as is understandable for Greeks, as “faithfulness.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn26",
      "id": "fn26"
    }
  }, [_vm._v("26")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_vm._v("Some resistance against this interpretation offered by Luz appears in the discussion of this passage by Matthean commentators W. D. Davies and D. C. Allison. They argue that "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" cannot mean faithfulness because Matthew never really means it that way elsewhere. Thus, it should be translated “faith.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn27",
      "id": "fn27"
    }
  }, [_vm._v("27")])]), _vm._v(" What Davies and Allison seem to be missing, though, is that the Matthean Jesus implies that justice, mercy, and "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" are things that the scribes and Pharisees have failed to "), _c('i', [_vm._v("do")]), _vm._v(" ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ποιέω")]), _vm._v("). Elsewhere in Matthew (when he refers to “"), _c('i', [_vm._v("believing")]), _vm._v(" faith”), verbs of "), _c('i', [_vm._v("doing")]), _vm._v(" (like "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ποιέω")]), _vm._v(") are not associated with "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(", but it would be altogether appropriate in 23:23, where there is an ethical core to how "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" is being used."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn28",
      "id": "fn28"
    }
  }, [_vm._v("28")])])]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("While I have only touched on the meaning of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" as “obeying faith,” something that one "), _c('i', [_vm._v("does")]), _vm._v(", it should prove sufficient for now to demonstrate the more "), _c('i', [_vm._v("active")]), _vm._v(" nature of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" at least in some instances.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The challenge with the polyvalent nature of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(", though, is not that "), _c('a', {
    attrs: {
      "id": "page_12"
    }
  }, [_vm._v("it can mean two different things (a similar situation is seen with "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("παῖς")]), _vm._v(", which can mean “servant” or “child”; it obviously means only one or the other in virtually all circumstances, so context usually clarifies which meaning is intended in a particular discourse). "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις")]), _vm._v(" is far more complex. The two possible meanings ("), _c('i', [_vm._v("believing")]), _vm._v(" faith and "), _c('i', [_vm._v("obeying")]), _vm._v(" faith) of this word are related and sometimes, perhaps often, blended or indistinguishable in usage. Thus, we inevitably must account for this by bringing in a third meaning or value of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(", which somehow meets the two meanings in the middle or perhaps widens to overlap them both.")])]), _c('a', {
    attrs: {
      "id": "page_12"
    }
  }, [_c('p', {
    staticClass: "H2"
  }, [_vm._v("Meaning #3: Trusting Faith ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις")]), _vm._v(" as “Trust”)")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("It is helpful to think of the meaning of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" not in isolated zones, but rather along a kind of spectrum:")]), _vm._v(" "), _c('div', {
    staticClass: "group_image"
  }, [_c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "image",
      "src": "images/image1.jpg"
    }
  })])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("The problem is that most of the time Paul is not wanting to refer to just one extreme of this semantic line, though there is this possibility. Where Paul’s meaning in any given contextualized usage lies must be determined on a case-by-case basis. Instead of thinking about the semantics of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in zone terms, we must consider that his meaning may "), _c('i', [_vm._v("modulate")]), _vm._v(", moving across this spectrum according to his intended meaning.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("If Paul does not always want to land on one side of this pole, how should we represent that? There may be times (I would argue "), _c('i', [_vm._v("many")]), _vm._v(" times) where we must recognize a meaning of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Paul that tries to encapsulate both of these polarized values. We can call this “"), _c('i', [_vm._v("trusting")]), _vm._v(" faith”:")]), _vm._v(" "), _c('div', {
    staticClass: "group_image"
  }, [_c('p', {
    staticClass: "image"
  }, [_c('img', {
    attrs: {
      "alt": "image",
      "src": "images/image2.jpg"
    }
  })])])]), _c('p', {
    staticClass: "Bodyflush1"
  }, [_c('a', {
    attrs: {
      "id": "page_12"
    }
  }, [_vm._v("Why the choice of the word “trust”? After all, it is relatively unpopular as a rendering of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in English translations of the Bible. Richard Hays advocates for the use of the English word “trust” to represent "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" (generally speaking) ")]), _c('a', {
    attrs: {
      "id": "page_13"
    }
  }, [_vm._v("precisely because it heals the breach, so to speak, between the cognitive aspects of the word’s possible meaning and the active (behavioral/practical) valence of the word. Hays remarks that he prefers “trust” over “have faith” especially because there is a tendency to read faith/belief language as a “subjective attitude of the individual believer” that could mislead readers of Paul into thinking that the apostle places his soteriological emphasis on held beliefs rather than relational "), _c('i', [_vm._v("trust")]), _vm._v("."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn29",
      "id": "fn29"
    }
  }, [_vm._v("29")]), _vm._v(" Hays finds “trust” especially appropriate to articulate Pauline "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" because it can carry at the same time the cognitive dimensions of choosing to think rightly about God as well as the covenantal dimensions of commitment to obedience.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("While it is difficult to parcel out the differences between belief, trust, and faithfulness in any precise way, I artificially use the following model for heuristic purposes:")]), _vm._v(" "), _c('ul', [_c('li', [_vm._v("Believing faith is cognitively active: believing is something you do with your mind. It is a function of thought (cf. Matt 21:32).")]), _vm._v(" "), _c('li', [_vm._v("Obeying faith is relationally active: faithfulness is understood in this discussion as an active form of loyalty and obedience. As noted above, scholars tend to associate it with virtues and ethical practices like love and perseverance. Insofar as it may be observed, we can say it is a function of the active self, the self-in-relationship where loyalty can be demonstrated.")]), _vm._v(" "), _c('li', [_vm._v("Trusting faith is volitionally active: volition, or will, is a calculated word choice for trusting faith because we use the word “will” in English to represent something preactive (“will to act”) and also sometimes active in itself (“goodwill”). The work of the will appears central to the relational commitment of a person to another and, thus, perfectly captures how Paul seems to use "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in a large number of cases.")])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_vm._v("While it would prove difficult in some circumstances to designate a particular occurrence of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" as one of these options in a neat way, still it helps the reader of Paul theoretically to keep in mind the unique way that "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" operates as a polyvalent noun that can modulate across a spectrum of semantic nuances.")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s6"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_14"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s6"
    }
  }, [_vm._v("Is This a Book about the "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις Χριστοῦ")]), _vm._v(" (Faith of Christ) Debate?")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("When I first began research for this book and I shared with colleagues my interest in exploring Paul’s language of faith, the most common response was: "), _c('i', [_vm._v("so you are writing on the "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις Χριστοῦ")]), _vm._v(" debate?")]), _vm._v(" That certainly says something about the longevity and intensity of that particular scholarly discussion, but that has never been a direct interest of mine."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn30",
      "id": "fn30"
    }
  }, [_vm._v("30")])]), _vm._v(" First and foremost, I find that scholars and armchair readers of Paul often gloss over Paul’s faith language, presuming what he means (“religious beliefs”) rather than examining it in its unique semantic breadth and theological depth. Second, I want to place, as it were, Paul’s use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in relation to how Jews and pagans used the same word within and outside of religious discourse. It is easy to assume that faith is primarily a religious term, even a "), _c('i', [_vm._v("Christian")]), _vm._v(" term. But the reality is that Paul was largely influenced in his faith language by the Septuagint and the Jesus tradition. Now, of course, Paul was a dynamic thinker, so he was bound to put some fresh thought into the nature of faith, but it was far from a neologism.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("When it comes to "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις Χριστοῦ")]), _vm._v(", I decided not to address this issue as a central matter of this work, because my desire is to step back and take a panoramic perspective on Paul’s faith language without getting too bogged down in this particular academic squabble. Chapter 8 on Galatians gives some attention to this discussion, but for those interested in my interpretation of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις Χριστοῦ")]), _vm._v(" and the nature of the debate, see chapter 10.")]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s7"
    }
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s7"
    }
  }, [_c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις")]), _vm._v(" and the Divine and Human Agency in Paul Debate")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("At the risk of neglecting the "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις Χριστοῦ")]), _vm._v(" debate, I focus on what Paul’s faith language has to say about his understanding of the believer’s relationship with God and with Jesus Christ. Paul’s use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" can inform the lively academic discussions that are taking place especially regarding “divine and human agency” in Paul."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn31",
      "id": "fn31"
    }
  }, [_vm._v("31")])]), _vm._v(" On that matter, scholars look at Paul’s language of salvation, works, justification, and grace. Unfortunately, "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" has not been "), _c('a', {
    attrs: {
      "id": "page_15"
    }
  }, [_vm._v("given much attention."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn32",
      "id": "fn32"
    }
  }, [_vm._v("32")]), _vm._v(" Some argue that Paul’s use of faith points to the (passive) acceptance of salvation, apart from works. Others posit that faith refers "), _c('i', [_vm._v("not")]), _vm._v(" to something humans do but to the faithfulness of Christ, such that human faith is not really that important in Paul’s soteriological framework. In both of these cases, it is as if soteriological agency is viewed as a mathematical equation: [human input] + [divine input] = [salvation]. In the minds of some scholars, any attempt to take "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" seriously as something humans "), _c('i', [_vm._v("do")]), _vm._v(" automatically "), _c('i', [_vm._v("decreases")]), _vm._v(" the amount one can put in the divine category and therefore could be labeled “(semi-)Pelagian” or “synergistic.” But how reasonable is the assumption that Paul thought of salvation as a zero-sum equation? Again, a more thorough study of Paul’s use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" has some very important things to say about this issue, and we will have several occasions to reflect on this.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("One major consideration pertaining to Pauline religion and the divine-human relationship involves how "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" relates to the notion of covenant. It is often said that the word “covenant” plays little to no role in Paul’s thought, and this appears to be true when we look at his meager use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("διαθήκη")]), _vm._v(" in particular (Gal 3:15, 17; 4:24; Rom 9:4; 11:27; 1 Cor 11:25; 2 Cor 3:6, 14; cf. Eph 2:12). But it is instructive to look at patterns and developments in early Judaism and its Greek literature, where we see a limited use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("διαθήκη")]), _vm._v(" in nonbiblical Jewish literature (see chap. 3) and a desire among Jewish writers to use more generic relational language (including "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(") to refer to a covenantlike religious sociology. An important and instructive example comes from the Septuagint, specifically Neh 10:1 (2 Esdr 19:38, numbered 20:1 in some versions of the Septuagint).")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("To give a brief bit of context, Neh 8–10 recounts the covenant-renewal ceremony that preceded the dedication of the rebuilt walls of Jerusalem for the returned exiles. In 9:38 the signatories proclaim:")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_vm._v("Because of all this we make a firm agreement in writing, and on that sealed document are inscribed the names of our officials, our Levites, and our priests. (NRSV)")]), _vm._v(" "), _c('p', {
    staticClass: "EX"
  }, [_c('a', {
    attrs: {
      "id": "page_16"
    }
  }, [_vm._v("And in regard to all these circumstances we make a covenant, and write "), _c('i', [_vm._v("it")]), _vm._v(", and our princes, our Levites, "), _c('i', [_vm._v("and")]), _vm._v(" our priests, set their seal to "), _c('i', [_vm._v("it")]), _vm._v(". (LXX, trans. Brenton)")])]), _c('a', {
    attrs: {
      "id": "page_16"
    }
  }, [_c('p', {
    staticClass: "EX"
  }, [_c('span', {
    staticClass: "grk"
  }, [_vm._v("καὶ ἐν πᾶσι τούτοις ἡµεῖς διατιθέµεθα πίστιν καὶ γράφοµεν")]), _vm._v(", "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("καὶ ἐπισφραγίζουσιν πάντες ἄρχοντες ἡµῶν")]), _vm._v(", "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Λευῖται ἡµῶν")]), _vm._v(", "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ἱερεῖς ἡµῶν")]), _vm._v(". (Rahlfs)")])]), _c('p', {
    staticClass: "Bodyflush1"
  }, [_c('a', {
    attrs: {
      "id": "page_16"
    }
  }, [_vm._v("The Hebrew phrasing here for “make a covenant” is "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("כרתים אמנה")]), _vm._v(", which literally means “to cut a firmness.” This is unusual phrasing; normally you “cut” a covenant ("), _c('span', {
    staticClass: "heb"
  }, [_vm._v("ברית")]), _vm._v("). Here "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמנה")]), _vm._v(" appears to serve as a kind of circumlocution for the expected word “covenant.” The Septuagint sometimes took a very mechanical and rigid approach to translating the Hebrew text. So "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" would have been a natural choice. But why did the "), _c('i', [_vm._v("Hebrew")]), _vm._v(" writer choose "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמנה")]), _vm._v(" in the first place? To be honest, it is unclear, but one explanation is that here we do not have the institution of a brand new covenant but rather a renewal ceremony."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn33",
      "id": "fn33"
    }
  }, [_vm._v("33")]), _vm._v(" Be that as it may, why would the author use "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("כרת")]), _vm._v(" then?")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("The Greek translator chose a word for establishing ("), _c('span', {
    staticClass: "grk"
  }, [_vm._v("διατιθέµεθα")]), _vm._v(") to clarify the general contractual nature of the situation, and then did what was rather comfortable by rendering "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמנה")]), _vm._v(" as "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(". Despite the strange phrasing of the Hebrew text (which is only slightly less awkward in Greek), we can conclude that in the postexilic period "), _c('span', {
    staticClass: "heb"
  }, [_vm._v("אמנה")]), _vm._v(" could serve to represent a relational bond, something similar to a covenant."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn34",
      "id": "fn34"
    }
  }, [_vm._v("34")])]), _vm._v(" The use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" here corresponds to pagan and mundane use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in antiquity, where it often refers to relationships of trust, fidelity, and goodwill. It makes sense, then, that "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" became a term that approximated the covenantal bond, both in the Septuagint and in other Hellenistic Jewish literature (see chap. 3). This might help us consider why Paul does not use explicit covenant language in a formative or central way in the letters, and we also might entertain the thought that Paul’s faith language may overlap significantly with the Jewish concept of covenant. This has great potential to inform conversations about divine and human agency in Paul. Consider how biblical scholars reflect on the concept of covenant in the Old Testament and how that might inform or reshape how we approach the divine-human relationship in Paul, "), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }, [_vm._v("especially regarding the tendency to quantify agency. Biblical covenants are not business contracts that operate according to a quid pro quo social economy. In the Jewish covenants, God committed himself to Israel out of love (Deut 7:8), and despite Israel’s agreeing to obedience, sin was not a deal breaker, so to speak. This statement captures well the covenantal dynamic:")])]), _c('a', {
    attrs: {
      "id": "page_17"
    }
  }), _c('p', {
    staticClass: "EX"
  }, [_c('a', {
    attrs: {
      "id": "page_17"
    }
  }, [_vm._v("If the relationship between Israel and Yahweh had been the kind conveyed in a modern contract, Yahweh’s commitment would have been contingent upon Israel’s keeping of its obligations. In the covenant relationship, Yahweh honors his part (the promises) because of his love and because he is God. The Lord may punish Israel for disobedience, and may even chasten whole generations for stubborn disbelief. But the covenant remains in force—simply because of God’s nature."), _c('sup')]), _c('a', {
    attrs: {
      "href": "#/xhtml/#rfn35",
      "id": "fn35"
    }
  }, [_vm._v("35")])]), _vm._v(" "), _c('p', {
    staticClass: "Body1"
  }, [_vm._v("Contracts depend on legal compliance and sanctions—that is not a bad thing when it comes to legal rights and commercial transactions. But that is not how the Jewish holy covenants operated. They involved what David Blumenthal refers to as a fusion of faith and grace: “Grace evokes faith and faith evokes grace; acts of unmerited love call forth acts of faithfulness, while acts of faithfulness call forth acts of unmerited love—in all dimensions of existence. Thus, god loves us in grace and we are faithful to him and, vice versa, we are faithful to him and he loves us in covenantal grace.”"), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn36",
      "id": "fn36"
    }
  }, [_vm._v("36")])]), _vm._v(" Blumenthal sums up by quoting Abraham Heschel: “"), _c('i', [_vm._v("Faith is sensitivity, understanding, engagement, and attachment")]), _vm._v(".” One can see immediately from such a perspective how this Jewish way of thinking about faith and covenant can illuminate Paul’s use of faith language. Passive is never a way that an Israelite or Jew would have thought of faith in God. When we encounter what seems like nonactive expressions of faith language, we might be better off speaking of faith as an “active mode of receptivity,” or a will that welcomes, invites, defers, and receives with attentiveness and responsiveness."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn37",
      "id": "fn37"
    }
  }, [_vm._v("37")])])]), _vm._v(" "), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s8"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_18"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s8"
    }
  }, [_vm._v("A Question of Method")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("What is the best way to go about this study of Paul’s faith language? Anything that attempts to be exegetical theology will, of necessity, be multidimensional in approach. Below are the operating principles and methods with which I work throughout the book.")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("A Semantic-Domain Approach to Word Meanings")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Many traditional lexicons and dictionaries work with a haphazard approach to the delineation of word meanings. To understand and make meaning of how words are "), _c('i', [_vm._v("used")]), _vm._v(", we do well to think in terms of semantic categories or domains and words like "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" clearly demonstrate a breadth of usage that covers several broader domains. In their introduction to "), _c('i', [_vm._v("Greek-English Lexicon: Based on Semantic Domains")]), _vm._v(", J. P. Louw and Eugene Nida note that traditional lexicons do not recognize that “the different meanings of a single word are relatively far apart in semantic space.” Using the example of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πνεῦµα")]), _vm._v(", they point out eight different semantic nuances that cross several broader categories."), _c('sup', [_c('a', {
    attrs: {
      "href": "#/xhtml/#rfn38",
      "id": "fn38"
    }
  }, [_vm._v("38")])]), _vm._v(" This is crucial for our study of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v("; as already noted, Paul could use this word to talk about several different kinds of things: a way of thinking, trust in Jesus Christ, social fidelity to another person, and so on.")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_vm._v("Cultural Linguistics")]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("This study adheres to the notion that the words that humans choose are deeply connected to their cultural heritage and assumptions. When we talk about "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Paul, we must do the best we can to place the usage of that term into its wider encyclopedia in a particular time and culture. This does not dictate the exact meaning of the word in a given situation, but it does tie the word to its history of usage and expected connotations in a general sense.")]), _vm._v(" "), _c('p', {
    staticClass: "H2"
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_vm._v("Contextualized Meaning")])]), _c('a', {
    attrs: {
      "id": "page_19"
    }
  }, [_c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("One of the obstacles to studying key words in Paul (generally) is the way readers can flatten out the use of a word and ignore the specific text’s literary context. But, of course, this is precisely the most important factor for discerning the exact meaning of a word like "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in any given occasion. Therefore, I examine several uses of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in Paul with close attention to his communicative purposes in their discrete contexts. This will allow the nuances of that situation to color "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in different ways. That does not prevent one from stepping back and observing broader patterns and repeated theological themes, but the temptation to make general statements ought not to shortcut the process of case-by-case, passage-by-passage, examination.")])]), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s9"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_19"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s9"
    }
  }, [_vm._v("Looking Ahead")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("Before diving into Paul’s letters, I begin our study in chapter 2 with a brief history of the interpretation of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" and faith language in Paul. It is probably no surprise that Luther and Bultmann played pivotal roles in shaping how Protestant scholarship read and interpreted "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in biblical discourse. Next (chap. 3) I offer various soundings in the ways that "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" is used in pagan and Jewish Greek texts of antiquity. This offers a perspective on the breadth of usage and also interesting patterns and developments specifically in Jewish literature. After that I examine "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in the Jesus tradition (chap. 4). In some ways this might seem anachronistic, since 1 Thessalonians and Galatians almost certainly predate the canonical Gospels. Still, given how central faith language is to the Jesus tradition, we can assume that this would have played some formative role in how Paul thought about and used faith language.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("These initial chapters set the stage for a series of studies on "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" and faith language in individual Pauline texts. In chapter 5 I examine selections from 1 Thessalonians and Philippians, letters that tend to prefer a meaning of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" that is close to that notion of (human) faithfulness. Chapters 6 and 7 respectively cover faith language in the two Corinthians epistles. While there is not one consistent meaning for "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in any of Paul’s letters, nevertheless, I make the case that the more cognitive nuance of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" is showcased well in some specific cases in the Corinthian letters, especially as faith relates to wisdom and epistemological transformation. Chapter 8 is a lengthy study of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" "), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_vm._v("in Galatians and addresses with more detail how Paul’s faith language relates to divine and human agency and what has come to be known as “covenantal nomism.” Chapter 9 addresses Rom 1:17 in relation to the quotation of Hab 2:4 (“the "), _c('i', [_vm._v("righteous")]), _vm._v(" will live by faith”). This case study offers an opportunity to focus on a leading thesis statement in Rom 1:16–17 and also an Old Testament text central to Paul’s theology of faith. Within these chapters on Paul’s letters (chaps. 5–9), it would be impossible to catalog and discuss each and every use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(". What I provide is a series of vignettes that highlight a verse or section of an epistle that can serve as a focal point for reflection on how he uses "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" language semantically and rhetorically.")])]), _c('a', {
    attrs: {
      "id": "page_20"
    }
  }, [_c('p', {
    staticClass: "Body"
  }, [_vm._v("Chapter 10 engages with the "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις Χριστοῦ")]), _vm._v(" debate. In chapter 11, I bring together the different threads of insight from the previous chapters and address at a broader level how this might shape how we approach Paul and his language of faith.")])]), _c('p', {
    staticClass: "H1",
    attrs: {
      "id": "s10"
    }
  }, [_c('a', {
    attrs: {
      "id": "page_20"
    }
  }), _c('a', {
    attrs: {
      "href": "#/xhtml/contents#ch1s10"
    }
  }, [_vm._v("How to Navigate This Book: A Note for Different Readers")])]), _vm._v(" "), _c('p', {
    staticClass: "Bodyflush1"
  }, [_vm._v("When I wrote this book, I had in mind the academic conversations about Paul’s theology that happen in the guild of Pauline studies. There is much misunderstanding about Paul’s use of faith language and ignorance about both the Jewish and pagan use of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in antiquity as well as the early reception of Paul’s faith language; thus, the first two main chapters (chaps. 2–3) are necessarily lengthy and technical. I encourage scholars to read that material carefully and patiently.")]), _vm._v(" "), _c('p', {
    staticClass: "Body"
  }, [_vm._v("There may be other readers who are interested in Paul’s theology but do not want to wade through some of the more dense material at the beginning. For those, I give you permission to skip ahead to chapter 4. Of course, this book builds the chapters on Paul’s letters on those preliminary discussions and arguments, but I think the rest of the chapters are readable. If someone wishes to pick up on the main arguments I make in this book about Paul’s use of faith language and how it relates to his theology—especially his understanding of divine and human agency—some readers might do well to read the conclusion (chap. 11) "), _c('i', [_vm._v("first")]), _vm._v(" and then turn to the main chapters.")]), _vm._v(" "), _c('p', {
    staticClass: "line"
  }, [_c('img', {
    attrs: {
      "alt": "line",
      "src": "images/line1.jpg"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn1",
      "id": "rfn1"
    }
  }, [_vm._v("1")]), _vm._v(". Augustine: “Ubi deficit ratio, ibi est fidei aedificatio”; Sermon 247, author’s translation.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn2",
      "id": "rfn2"
    }
  }, [_vm._v("2")]), _vm._v(". Mark Boespflug makes a cogent case for understanding Augustine’s conception of faith to be rational rather than suprarational; see “Is Augustinian Faith Rational?” "), _c('i', [_vm._v("Religious Studies")]), _vm._v(" 52 (2016): 63–79.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn3",
      "id": "rfn3"
    }
  }, [_vm._v("3")]), _vm._v(". Luke Timothy Johnson, "), _c('i', [_vm._v("The Creed")]), _vm._v(" (New York: Doubleday, 2003), 45. Johnson is concerned with a proper understanding of the church’s ecumenical creeds; but he bases the foundation of the faith language of the creeds ("), _c('i', [_vm._v("credo")]), _vm._v(", "), _c('i', [_vm._v("credere")]), _vm._v(") on the importance of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" in the New Testament, which is made clear in this quotation.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn4",
      "id": "rfn4"
    }
  }, [_vm._v("4")]), _vm._v(". Johnson, "), _c('i', [_vm._v("Creed")]), _vm._v(", 44.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn5",
      "id": "rfn5"
    }
  }, [_vm._v("5")]), _vm._v(". Martin Luther, "), _c('i', [_vm._v("Commentary on Romans")]), _vm._v(", Luther’s Works, ed. Jaroslav Pelikan and Helmut T. Lehmann (Philadelphia: Fortress, 1972), 34.336–38.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn6",
      "id": "rfn6"
    }
  }, [_vm._v("6")]), _vm._v(". Alister McGrath, "), _c('i', [_vm._v("Studies in Doctrine")]), _vm._v(" (Grand Rapids: Zondervan, 1997), 390.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn7",
      "id": "rfn7"
    }
  }, [_vm._v("7")]), _vm._v(". Walter C. Kaiser, "), _c('i', [_vm._v("The Christian and the Old Testament")]), _vm._v(" (Pasadena CA: William Carey Library, 1998), 54; cf. Grant Osborne, "), _c('i', [_vm._v("Romans")]), _vm._v(" (Downers Grove, IL: InterVarsity, 2004), 281: “Faith is not a ‘work,’ but a passive opening of oneself up to the work of the Spirit within.”")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn8",
      "id": "rfn8"
    }
  }, [_vm._v("8")]), _vm._v(". See especially T. Michael Law, "), _c('i', [_vm._v("When God Spoke Greek")]), _vm._v(" (Oxford: Oxford University Press, 2013).")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn9",
      "id": "rfn9"
    }
  }, [_vm._v("9")]), _vm._v(". See James D. G. Dunn, "), _c('i', [_vm._v("The Theology of Paul the Apostle")]), _vm._v(" (Grand Rapids: Eerdmans, 1998), 373n159.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn10",
      "id": "rfn10"
    }
  }, [_vm._v("10")]), _vm._v(". Marvin Wilson, "), _c('i', [_vm._v("Our Father Abraham")]), _vm._v(" (Grand Rapids: Eerdmans, 1989), 183.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn11",
      "id": "rfn11"
    }
  }, [_vm._v("11")]), _vm._v(". T. F. Torrance linked this Old Testament term to the expression of “the reality of God in covenant-relationship”; see “One Aspect of the Biblical Conception of Faith,” "), _c('i', [_vm._v("Expository Times")]), _vm._v(" 68 (1957): 111–14 at 112.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn12",
      "id": "rfn12"
    }
  }, [_vm._v("12")]), _vm._v(". Walter Brueggemann, "), _c('i', [_vm._v("Reverberations of Faith")]), _vm._v(" (Louisville: Westminster John Knox, 2002), 78.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn13",
      "id": "rfn13"
    }
  }, [_vm._v("13")]), _vm._v(". See Varghese P. Chiraparamban, “The Translation of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πίστις")]), _vm._v(" and Its Cognates in the Pauline Epistles,” "), _c('i', [_vm._v("Bible Translator")]), _vm._v(" 66 (2015): 176–89.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn14",
      "id": "rfn14"
    }
  }, [_vm._v("14")]), _vm._v(". Richard N. Longenecker, "), _c('i', [_vm._v("Galatians")]), _vm._v(", World Biblical Commentary 41 (Grand Rapids: Zondervan, 1990), 262.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn15",
      "id": "rfn15"
    }
  }, [_vm._v("15")]), _vm._v(". Douglas J. Moo, "), _c('i', [_vm._v("The Epistle to the Romans")]), _vm._v(", New International Commentary on the New Testament, 2nd ed. (Grand Rapids: Eerdmans, 2018), 245–46.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn16",
      "id": "rfn16"
    }
  }, [_vm._v("16")]), _vm._v(". One often sees this worked out in interpretations of Rom 4:5, where faith and work are ostensibly put at odds.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn17",
      "id": "rfn17"
    }
  }, [_vm._v("17")]), _vm._v(". Markus Barth, "), _c('i', [_vm._v("The Letter to Philemon")]), _vm._v(", Eerdmans Critical Commentary (Grand Rapids: Eerdmans, 2010), 273.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn18",
      "id": "rfn18"
    }
  }, [_vm._v("18")]), _vm._v(". Barth, "), _c('i', [_vm._v("Philemon")]), _vm._v(", 271–74.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn19",
      "id": "rfn19"
    }
  }, [_vm._v("19")]), _vm._v(". On this point, see R. Barry Matlock, “Detheologizing the "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("ΠΙΣΤΙΣ ΧΡΙΣΤΟΥ")]), _vm._v(" Debate: Cautionary Remarks from a Lexical Semantic Perspective,” "), _c('i', [_vm._v("Novum Testamentum")]), _vm._v(" 42 (2000): 13–15.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn20",
      "id": "rfn20"
    }
  }, [_vm._v("20")]), _vm._v(". There are a few interesting deviations from this in certain translations. In Gal 3:9, some translators prefer to render "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("τῷ πιστῷ Ἀβραάµ")]), _vm._v(" as “Abraham the believer” (NET) or “man of faith” (ESV) rather than the more natural translation “the faithful Abraham” (KJV, ASV). Even though the most straightforward translation of "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πιστός")]), _vm._v(" is “faithful,” most translations opt for “faith” or “believer” because Paul places the emphasis in Galatians, not on Abraham’s covenant faithfulness, but precisely on his initial faith and trust in God. Thus, even though “Abraham the believer” makes sense in 3:9 contextually, such an English translation flattens out the almost provocative use of the word "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πιστός")]), _vm._v(" in his argument. For a discussion of the nuances involved in this word choice, see Paul Trebilco, "), _c('i', [_vm._v("Self-Designations and Group Identity in the New Testament")]), _vm._v(" (Cambridge: Cambridge University Press, 2012), 87. See also chap. 8 below on Galatians.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn21",
      "id": "rfn21"
    }
  }, [_vm._v("21")]), _vm._v(". Barth, "), _c('i', [_vm._v("Philemon")]), _vm._v(", 273.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn22",
      "id": "rfn22"
    }
  }, [_vm._v("22")]), _vm._v(". Barth, "), _c('i', [_vm._v("Philemon")]), _vm._v(", 273.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn23",
      "id": "rfn23"
    }
  }, [_vm._v("23")]), _vm._v(". David Garland expresses the heart of Paul’s point in 2 Cor 5:6–8 regarding the proper perspective on his apostolic weakness: “Living by faith and not by sight means that as we walk with God we have no literal pillar of cloud or fire to guide us as Israel of old had. Paul believed that our life is hidden, so he cannot prove it from outward appearances. And those who judge things only by outward appearances (physical weaknesses, suffering, near death experiences) cannot see the whole truth about him or any other Christian. Only faith can take measure of the unseen eternal realities of the next aeon, where all this present weakness and mortality will be transformed into something sublime”; see "), _c('i', [_vm._v("2 Corinthians")]), _vm._v(", New American Commentary (Nashville: Broadman & Holman, 1999), 265.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn24",
      "id": "rfn24"
    }
  }, [_vm._v("24")]), _vm._v(". Barth, "), _c('i', [_vm._v("Philemon")]), _vm._v(", 273.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn25",
      "id": "rfn25"
    }
  }, [_vm._v("25")]), _vm._v(". Most translations render "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις")]), _vm._v(" “faithfulness” here (NASB, NIV, ESV, NET), but a few prefer “faith” (KJV, RSV, NRSV).")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn26",
      "id": "rfn26"
    }
  }, [_vm._v("26")]), _vm._v(". Ulrich Luz, "), _c('i', [_vm._v("Matthew 21–28")]), _vm._v(", Hermeneia (Minneapolis: Fortress, 2005), 124.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn27",
      "id": "rfn27"
    }
  }, [_vm._v("27")]), _vm._v(". See W. D. Davies and Dale C. Allison, "), _c('i', [_vm._v("Matthew")]), _vm._v(", International Critical Commentary (Edinburgh: T&T Clark, 1997), 3:294.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn28",
      "id": "rfn28"
    }
  }, [_vm._v("28")]), _vm._v(". R. T. France should be given credit for this insight; see "), _c('i', [_vm._v("The Gospel according to Matthew")]), _vm._v(", New International Commentary on the New Testament (Grand Rapids: Eerdmans, 2007), 873–74.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn29",
      "id": "rfn29"
    }
  }, [_vm._v("29")]), _vm._v(". Richard B. Hays, “Lost in Translation: A Reflection on Romans in the Common English Bible,” in "), _c('i', [_vm._v("The Unrelenting God")]), _vm._v(", ed. David J. Downs and Matthew L. Skinner (Grand Rapids: Eerdmans, 2014), 92–94.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn30",
      "id": "rfn30"
    }
  }, [_vm._v("30")]), _vm._v(". Peter Oakes contends that the "), _c('span', {
    staticClass: "grk"
  }, [_vm._v("πίστις Χριστοῦ")]), _vm._v(" debate created a logjam in the study of faith language in Paul; see “"), _c('span', {
    staticClass: "grk"
  }, [_vm._v("Πιστις")]), _vm._v(" as Relational Way of Life in Galatians,” "), _c('i', [_vm._v("Journal for the Study of the New Testament")]), _vm._v(" 40 (2018): 255–75 at 257.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn31",
      "id": "rfn31"
    }
  }, [_vm._v("31")]), _vm._v(". See John M. G. Barclay and Simon Gathercole, eds., "), _c('i', [_vm._v("Divine and Human Agency in Paul and His Cultural Environment")]), _vm._v(" (London: T&T Clark, 2006).")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn32",
      "id": "rfn32"
    }
  }, [_vm._v("32")]), _vm._v(". Although there are some important recent treatments; see Benjamin Schliesser, "), _c('i', [_vm._v("Was Ist Glaube? Paulinische Perspektiven")]), _vm._v(" (Zurich: Theologischer Verlag, 2011); and Teresa Morgan, "), _c('i', [_vm._v("Roman Faith and Christian Faith: Pistis and Fides in the Early Roman Empire and the Early Churches")]), _vm._v(" (Oxford: Oxford University Press, 2015).")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn33",
      "id": "rfn33"
    }
  }, [_vm._v("33")]), _vm._v(". Philip Noss and Kenneth Thompson, "), _c('i', [_vm._v("A Handbook on Ezra and Nehemiah")]), _vm._v(" (New York: United Bible Societies, 2005), 461.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn34",
      "id": "rfn34"
    }
  }, [_vm._v("34")]), _vm._v(". See Andrew E. Steinmann, "), _c('i', [_vm._v("Ezra and Nehemiah")]), _vm._v(" (St. Louis: Concordia, 2010), 530. L. H. Brockington is more confident that this can refer to a covenant; see "), _c('i', [_vm._v("Ezra, Nehemiah, and Esther")]), _vm._v(", New Century Bible (London: Nelson, 1969), 177.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn35",
      "id": "rfn35"
    }
  }, [_vm._v("35")]), _vm._v(". William S. LaSor, David Allan Hubbard, and Frederic William Bush, "), _c('i', [_vm._v("Old Testament Survey")]), _vm._v(" (Grand Rapids: Eerdmans, 1982), 122.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn36",
      "id": "rfn36"
    }
  }, [_vm._v("36")]), _vm._v(". David Blumenthal, “The Place of Faith and Grace in Judaism,” in "), _c('i', [_vm._v("A Time to Speak")]), _vm._v(", ed. James Rudin and Marvin R. Wilson (Grand Rapids: Eerdmans, 1987), 104–14 at 111.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn37",
      "id": "rfn37"
    }
  }, [_vm._v("37")]), _vm._v(". See Walther Eichrodt, “Covenant and Law,” "), _c('i', [_vm._v("Interpretation")]), _vm._v(" 20 (1966): 302–21 at 310.")]), _vm._v(" "), _c('p', {
    staticClass: "foot"
  }, [_c('a', {
    attrs: {
      "href": "#/xhtml/#fn38",
      "id": "rfn38"
    }
  }, [_vm._v("38")]), _vm._v(". Johannes P. Louw and Eugene Albert Nida, "), _c('i', [_vm._v("Greek-English Lexicon of the New Testament: Based on Semantic Domains")]), _vm._v(" (New York: United Bible Societies, 1996).")])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-3a8ba322","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/xhtml/chapter001.vue
// module id = 75
// module chunks = 0